import { ApolloClient, from, HttpLink, InMemoryCache, ApolloLink, split, Observable } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { createClient } from "graphql-ws";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { NetworkConfig } from "./NetworkConfig";
import { getRoleKey } from "./User";
import { AuthStore } from "src/stores/AuthStore";

export const createApolloClient = (authStore: AuthStore): ApolloClient<any> => {
    const httpLink = new HttpLink({
        uri: NetworkConfig.graphqlUrl
    });

    let wsConnectionToken: string | undefined;

    const wsClient = createClient({
        url: NetworkConfig.graphqlWssUrl,
        connectionParams: () => {
            wsConnectionToken = authStore.token;
            return {
                reconnect: true,
                headers: {
                    // Für den Ws-Link wird hier das Auth direkt gesetzt.
                    Authorization: authStore.token ? `Bearer ${authStore.token}` : "",
                    "x-hasura-role": getRoleKey(authStore.user?.role)
                }
            };
        },
        on: {
            closed: async (event) => {
                const closeEvent = event as CloseEvent;
                // 1000 is normal closure, 4403 is forbidden/auth error
                if (closeEvent.code === 4403) {
                    console.warn(
                        "WebSocket closed with 4403 Forbidden - attempting token refresh. " +
                            `Reason: ${closeEvent.reason || "No reason provided"}`
                    );

                    if (authStore.token && authStore.refreshToken) {
                        try {
                            const refreshPromise = authStore.refreshRejectedToken(wsConnectionToken);
                            const timeoutPromise = new Promise<boolean>((_, reject) =>
                                setTimeout(() => reject(new Error("Token refresh timeout")), 10000)
                            );

                            const reloginSuccess = await Promise.race([refreshPromise, timeoutPromise]);
                            if (!reloginSuccess) {
                                console.error("Token refresh failed after WebSocket 4403 error");
                            }
                        } catch (error) {
                            console.error("Error refreshing token after WebSocket 4403:", error);
                        }
                    } else if (authStore.token) {
                        console.error("No refresh token available after WebSocket 4403 error - session ended");
                        authStore.removeUser();
                    }
                } else if (closeEvent.code !== 1000) {
                    console.error(
                        `WebSocket closed unexpectedly - Code: ${closeEvent.code}, ` +
                            `Reason: ${closeEvent.reason || "No reason provided"}`
                    );
                }
            },
            error: (error) => {
                console.error("WebSocket error:", error);
            }
        },
        retryAttempts: 5,
        shouldRetry: (errOrCloseEvent: unknown) => {
            if (typeof errOrCloseEvent === "object" && errOrCloseEvent !== null && "code" in errOrCloseEvent) {
                const closeEvent = errOrCloseEvent as { code: number };
                // Don't retry on auth errors - let the closed handler refresh the token first
                // The WebSocket will reconnect lazily on the next subscription with the fresh token
                if (closeEvent.code === 4403) {
                    return false;
                }
                // Don't retry on normal closure
                if (closeEvent.code === 1000) {
                    return false;
                }
                // Don't retry on other close codes that indicate non-recoverable errors
                // e.g., 1002 (protocol error), 1003 (unsupported data), 1007-1009 (policy violations)
                // Allow retries for 1006 (abnormal closure) as it's often network-related
                if (
                    (closeEvent.code >= 1002 && closeEvent.code <= 1005) ||
                    (closeEvent.code >= 1007 && closeEvent.code <= 1009)
                ) {
                    return false;
                }
            }
            // Retry on network errors and timeouts
            return true;
        }
    });

    const wsLink = new GraphQLWsLink(wsClient);

    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === "OperationDefinition" && definition.operation === "subscription";
        },
        wsLink, // use wsLink for subscriptions only
        httpLink // use httpLink as default
    );

    const queryPerformanceLink = new ApolloLink((operation, forward) => {
        const definition = getMainDefinition(operation.query);
        const transaction = Sentry.startTransaction({
            name: operation.operationName,
            op:
                definition.kind === "OperationDefinition" && definition.operation === "subscription"
                    ? "subscription"
                    : "query",
            data: operation.variables
        });

        operation.setContext({ transaction });

        return forward(operation).map((data) => {
            const transaction = operation.getContext().transaction;
            transaction.finish();
            return data;
        });
    });

    // Proactive token expiration check - runs before each request
    const tokenCheckLink = new ApolloLink((operation, forward) => {
        // Check if token is about to expire before making the request
        if (authStore.shouldRefreshToken()) {
            return new Observable((observer) => {
                const refreshPromise = authStore.ensureValidToken();
                const timeoutPromise = new Promise<boolean>((_, reject) =>
                    setTimeout(() => reject(new Error("Token refresh timeout")), 10000)
                );

                Promise.race([refreshPromise, timeoutPromise])
                    .then((success) => {
                        if (!success) {
                            console.warn("Proactive token refresh failed. Token is expired and refresh failed.");
                            observer.error(new Error("Token refresh failed"));
                        } else {
                            const subscriber = {
                                next: observer.next.bind(observer),
                                error: observer.error.bind(observer),
                                complete: observer.complete.bind(observer)
                            };
                            forward(operation).subscribe(subscriber);
                        }
                    })
                    .catch((error) => {
                        observer.error(error);
                    });
            });
        }
        return forward(operation);
    });

    const authLink = setContext((_, { headers }) => {
        let manuallySetHasuraRole = undefined;

        if (headers && headers.hasOwnProperty("x-hasura-role")) {
            manuallySetHasuraRole = headers["x-hasura-role"];
        }

        const headerObject = authStore.token
            ? {
                  ...headers,
                  Authorization: authStore.token ? `Bearer ${authStore.token}` : "",
                  "x-hasura-role":
                      manuallySetHasuraRole ?? authStore.currentlyUsedRole ?? getRoleKey(authStore.user?.role)
              }
            : { ...headers };

        return {
            headers: headerObject
        };
    });

    // Used when networkError occurs
    const retryLink = new RetryLink({
        attempts: {
            max: 3
        }
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
        // Determine operation type
        const definition = getMainDefinition(operation.query);
        const operationType = definition.kind === "OperationDefinition" ? definition.operation : "unknown";

        if (graphQLErrors) {
            for (const err of graphQLErrors) {
                switch (err.extensions.code) {
                    case "invalid-jwt":
                    case "invalid-headers":
                        if (authStore.token && authStore.refreshToken) {
                            return new Observable((observer) => {
                                const rejectedAuthorization: string | undefined =
                                    operation.getContext().headers?.Authorization;
                                const refreshPromise = authStore.refreshRejectedToken(
                                    rejectedAuthorization?.replace(`${authStore.tokenType} `, "")
                                );
                                const timeoutPromise = new Promise<boolean>((_, reject) =>
                                    setTimeout(() => reject(new Error("Token refresh timeout")), 10000)
                                );

                                Promise.race([refreshPromise, timeoutPromise])
                                    .then((reloginSuccess) => {
                                        if (reloginSuccess && authStore.token) {
                                            console.log("Relogin durch ApolloClient mit refreshToken erfolgreich");
                                            operation.setContext(({ headers = {} }: any) => ({
                                                headers: {
                                                    ...headers,
                                                    authorization: `${authStore.tokenType} ${authStore.token}`
                                                }
                                            }));
                                        } else {
                                            throw new Error("Token refresh failed");
                                        }
                                    })
                                    .then(() => {
                                        console.log(`Retrying Operation ${operation.operationName}`);
                                        const subscriber = {
                                            next: observer.next.bind(observer),
                                            error: observer.error.bind(observer),
                                            complete: observer.complete.bind(observer)
                                        };
                                        forward(operation).subscribe(subscriber);
                                    })
                                    .catch((error) => {
                                        observer.error(error);
                                    });
                            });
                        }
                        break;
                    default:
                        // Log GraphQL errors
                        console.error(
                            `[GraphQL ${operationType}] ${operation.operationName} - ` +
                                `Code: ${err.extensions?.code || "unknown"}, ` +
                                `Message: ${err.message}, ` +
                                `Path: ${err.path ? JSON.stringify(err.path) : "N/A"}`
                        );
                        break;
                }
            }
        }
        if (networkError) {
            // Log network errors
            const errorMessage = networkError.message || String(networkError);
            const statusCode = (networkError as any).statusCode || (networkError as any).status || "unknown";
            console.error(
                `[Network ${operationType}] ${operation.operationName} - ` +
                    `Status: ${statusCode}, Message: ${errorMessage}`
            );
        }
    });

    const client = new ApolloClient({
        link: from([queryPerformanceLink, tokenCheckLink, errorLink, authLink, retryLink, splitLink]),
        cache: new InMemoryCache({
            typePolicies: {
                file_v_manager_files_overview: {
                    keyFields: ["id", "category"]
                },
                file_v_owner_files_overview: {
                    keyFields: ["id", "category"]
                },
                file_v_coowner_files_overview: {
                    keyFields: ["id", "category"]
                }
            }
        })
    });

    return client;
};
